import { useState, useEffect } from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";

// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import HeadingTitleItem from "@/components/HeadingTitleItem";
import InfoItem from "@/components/InfoItem";

// Icons
import { Pen } from "lucide-react";
import type { CompanyBoards } from "@/types/soe";
import { S3_BUCKET_LINK } from "@/components/constants";
import formatDate from "@/utils/formatDate";

export const Route = createFileRoute(
  "/_layout-private/account-management/soe/_information/board-of-directors/$id"
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      id: search.id as string | undefined,
      data: search.data as string | undefined,
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const params = Route.useParams();
  const { id, data } = Route.useSearch();
  const { company_management } = useAccessPermission();

  // States
  const [parsedData, setParsedData] = useState({} as CompanyBoards);

  // Effects
  useEffect(() => {
    if (data) {
      setParsedData(JSON.parse(data));
    }
  }, [data]);

  console.log("Params", params);
  console.log("parsedData", parsedData);

  return (
    <div className="space-y-[32px]">
      <HeadingTitleItem
        title="ຂໍ້ມູນຄະນະຜູ້ຖືຫຸ້ນ-ສະພາບໍລິຫານ"
        subTitle="ລາຍລະອຽດຂໍ້ມູນຄະນະຜູ້ຖືຫຸ້ນ-ສະພາບໍລິຫານຂອງວິສາຫະກິດ"
      />

      <div className="flex justify-between w-full">
        <div>
          <small>ຮບໂປຣຟາຍ</small>
          <Avatar className="w-[120px] h-[120px] mt-[17px]">
            <AvatarImage
              className="object-cover"
              src={S3_BUCKET_LINK + parsedData?.profileUrl}
            />
            <AvatarFallback>{parsedData?.firstName}</AvatarFallback>
          </Avatar>
        </div>
        {company_management.isUpdate && (
          <Button
            onClick={() =>
              navigate({
                to: "/account-management/soe/board-of-directors/mutate",
                params: { id: params.id },
                search: {
                  id: id,
                  data: data,
                },
              })
            }
          >
            <Pen className="mr-2" />
            ແກ້ໄຂ
          </Button>
        )}
      </div>

      <div className="space-y-[12px] w-full">
        <InfoItem
          label="ຊື່ ແລະ ນາມສະກຸນ:"
          value={`${parsedData?.firstName || "-"} ${parsedData?.lastName || ""}`}
        />
        <InfoItem
          label="ຕຳແໜ່ງ:"
          value={parsedData.boardPosition?.boardPosition || "-"}
        />
        <InfoItem label="ຂະແໜງການ:" value={parsedData?.sector || "-"} />
        <InfoItem label="ຕຳແໜ່ງລັດ:" value={parsedData?.govPosition || "-"} />

        <InfoItem
          label="ວັນເດືອນປີເກີດ:"
          value={
            parsedData?.birthDate
              ? formatDate({
                  date: parsedData.birthDate,
                  format: "DD/MM/YYYY",
                })
              : "-"
          }
        />
        <InfoItem
          label="ແຂວງ, ເມືອງ, ບ້ານເກີດ:"
          value={
            parsedData?.province?.province &&
            parsedData?.district?.district &&
            parsedData?.village
              ? `${parsedData.province.province}, ${parsedData.district.district}, ${parsedData.village}`
              : "-"
          }
        />
        <InfoItem
          label="ສະຖານະ:"
          value={`${parsedData?.tenureStatus?.tenureStatus || "-"} ${parsedData?.isGovExecutive === true ? "(ເປັນຜູ້ບໍລິຫານຝ່າຍລັດ)" : "(ເປັນຜູ້ບໍລິຫານຝ່າຍເອກະຊົນ)"}`}
        />
      </div>

      <HeadingTitleItem title="ຂໍ້ຕົກລົງ" subTitle="ລາຍລະອຽດຂໍ້ຕົກລົງ" />
      <div className="space-y-[12px] w-full">
        <InfoItem label="ເລກທີ:" value={parsedData?.agreementNo || "-"} />
        <InfoItem
          label="ລົງວັນທີ:"
          value={
            parsedData?.agreementDate
              ? formatDate({
                  date: parsedData.agreementDate,
                  format: "DD/MM/YYYY",
                })
              : "-"
          }
        />
      </div>

      <HeadingTitleItem
        title="ລະດັບການສຶກສາ"
        subTitle="ລາຍລະອຽດລະດັບການສຶກສາ"
      />
      <div className="space-y-[12px] w-full">
        <InfoItem
          label="ວິຊາສະເພາະພາຍໃນປະເທດ:"
          value={parsedData?.domesticSpecialty || "-"}
        />
        <InfoItem
          label="ວິຊາສະເພາະຕ່າງປະເທດ:"
          value={parsedData?.foreignSpecialty || "-"}
        />
        <InfoItem
          label="ລະດັບພາສາຕ່າງປະເທດ:"
          value={parsedData?.languageLevel || "-"}
        />
        <InfoItem
          label="ລະດັບທິດສະດີການເມືອງ:"
          value={parsedData?.theoryLevel || "-"}
        />
        <InfoItem
          label="ປະຫວັດການສຶກສາ:"
          value={parsedData?.education || "-"}
          style="detail"
        />
        <InfoItem
          label="ປະສົບການ:"
          value={parsedData?.experience || "-"}
          style="detail"
        />
      </div>

      <HeadingTitleItem
        title="ຂໍ້ມູນການອັບເດດ"
        subTitle="ລາຍລະອຽດການອັບເດດຂໍ້ມູນ"
      />

      <div className="space-y-[12px] w-full">
        <InfoItem
          label="ວັນທີສ້າງ:"
          value={
            parsedData?.createdAt
              ? formatDate({
                  date: parsedData.createdAt,
                  format: "DD/MM/YYYY",
                })
              : "-"
          }
        />
        <InfoItem label="ສ້າງໂດຍ:" value={parsedData?.createdBy || "-"} />
        <InfoItem
          label="ອັບເດດລ່າສຸດ:"
          value={
            parsedData?.updatedAt
              ? formatDate({
                  date: parsedData.updatedAt,
                  format: "DD/MM/YYYY",
                })
              : "-"
          }
        />
        <InfoItem label="ອັບເດດໂດຍ:" value={parsedData?.updatedBy || "-"} />
      </div>
    </div>
  );
}
